import type { GetHealthDashboardResponse } from "@wallyo/contracts/console/dashboard";
import { useEffect, useReducer, useState } from "react";
import { getHealthDashboard } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { formatDate } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { DashboardView } from "./DashboardView";

type State =
  | { status: "loading"; dashboard: GetHealthDashboardResponse | null }
  | { status: "ready"; dashboard: GetHealthDashboardResponse }
  | { status: "failed"; dashboard: GetHealthDashboardResponse | null; error: unknown };

/** Tableau de bord (VIEW_HEALTH_DASHBOARD): GET /console/dashboard, today in Conakry. */
export function DashboardScreen() {
  const { client } = useApi();
  const { me } = useStaff();
  const [state, setState] = useState<State>({ status: "loading", dashboard: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    let current = true;
    setState((was) => ({ status: "loading", dashboard: was.dashboard }));
    getHealthDashboard(client).then(
      (dashboard) => current && setState({ status: "ready", dashboard }),
      (error: unknown) => current && setState((was) => ({ status: "failed", dashboard: was.dashboard, error })),
    );
    return () => {
      current = false;
    };
  }, [client, reads]);

  const firstName = me.staffMember.name.split(/\s+/)[0] ?? me.staffMember.name;
  const day = state.dashboard ? new Date(`${state.dashboard.day}T12:00:00Z`) : new Date();
  const date = formatDate(day, { weekday: "long", day: "numeric", month: "long", timeZone: "Africa/Conakry" });

  return (
    <>
      <PageTop
        title={t("dashboard.greeting", { name: firstName })}
        sub={t("dashboard.subtitle", { date: date.charAt(0).toUpperCase() + date.slice(1) })}
      />
      <div className="btns">
        <button className="btn quiet small" type="button" onClick={read} disabled={state.status === "loading"}>
          {state.status === "loading" ? t("dashboard.refreshing") : t("dashboard.refresh")}
        </button>
      </div>
      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("dashboard.loadFailed")}</p>
          <ErrorNotice error={state.error} />
        </div>
      ) : null}
      {state.dashboard ? (
        <DashboardView dashboard={state.dashboard} />
      ) : state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("dashboard.loading")}</span>
        </div>
      ) : null}
    </>
  );
}
